import type { MaybeRefOrGetter, UseStorageOptions } from '@vueuse/core'
import { useSessionStorage } from '@vueuse/core'
import type { Ref, UnwrapRef } from 'vue'
import { ref, toValue } from 'vue'
import { DEFAULT_PAGE_SIZE } from '@lib/constants/tables.const'

type PaginationPayload<T> = {
  storageKey?: string
  initialFilters: T
  initialOrdering: string
}

type PaginationReturn<T> = {
  search: Ref<string>
  pageSize: Ref<number>
  selectedFilters: Ref<T> | Ref<UnwrapRef<T>, T | UnwrapRef<T>>
  page: Ref<number>
  ordering: Ref<string>
}

export default <T>({ storageKey, initialFilters, initialOrdering }: PaginationPayload<T>): PaginationReturn<T> => {
  const getKey = (name: string) => storageKey + '-' + name
  const maybeSessionStorageOrRef = <K>(
    key: string,
    initialValue: MaybeRefOrGetter<K>,
    options?: UseStorageOptions<K>,
  ) => (storageKey ? useSessionStorage(key, initialValue, options) : ref(toValue(initialValue)))

  const initialSearch: string = ''
  const search = maybeSessionStorageOrRef(getKey('search'), initialSearch)

  const initialPageSize = DEFAULT_PAGE_SIZE
  const pageSize = maybeSessionStorageOrRef(getKey('pageSize'), initialPageSize)

  const selectedFilters = maybeSessionStorageOrRef(getKey('filters'), initialFilters, { deep: true })

  const ordering = ref(initialOrdering)
  const page = ref(1)

  return {
    search,
    pageSize,
    selectedFilters,
    page,
    ordering,
  }
}
